import type { FilterNode, SearchRequestInput } from '@servicetim/shared';
import { useJobs, useProjects, useTicketCount, useTicketSearch } from '@servicetim/ui/api';
import {
  Badge,
  buttonVariants,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Page,
  PageHeader,
  ProblemBanner,
  Skeleton,
} from '@servicetim/ui/components';
import { hasTenantRole, useSessionClaims } from '@servicetim/ui/lib';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  FolderKanban,
  type Inbox,
  ListChecks,
  UserRound,
  UserRoundX,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { StatusBadge, UserChip } from '../tickets/badges';
import { relativeTime } from '../tickets/format';
import { TicketKeyLink } from '../tickets/ticket-link';

const OPEN: FilterNode = { field: 'status.category', op: 'neq', value: 'done' };
const MY_OPEN: FilterNode = { and: [{ field: 'assignee_id', op: 'eq', value: '$me' }, OPEN] };
const UNASSIGNED: FilterNode = { and: [{ field: 'assignee_id', op: 'is_null' }, OPEN] };
const RECENT: SearchRequestInput = {
  sort: [{ field: 'updated_at', dir: 'desc' }],
  select: ['id', 'key', 'summary', 'status_id', 'assignee_id', 'project_id', 'updated_at'],
  page: { size: 10 },
};

function StatCard({
  icon: Icon,
  title,
  description,
  value,
  isPending,
  error,
  to,
}: {
  icon: typeof Inbox;
  title: string;
  description: string;
  value: ReactNode;
  isPending: boolean;
  error?: unknown;
  to?: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <Icon className="size-5 text-brand-600" aria-hidden="true" />
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex items-end justify-between gap-2">
        {isPending ? (
          <Skeleton className="h-9 w-16" />
        ) : error ? (
          <span className="font-semibold text-3xl text-fg-muted" title="Could not load">
            —
          </span>
        ) : (
          <p className="font-semibold text-3xl text-fg tabular-nums">{value}</p>
        )}
        {to}
      </CardContent>
    </Card>
  );
}

const countValue = (data: { count: number; exact: boolean } | undefined): string =>
  data ? `${data.count.toLocaleString()}${data.exact ? '' : '+'}` : '—';

/** `/`: my open tickets, unassigned, recently updated, projects; admins also see counts of projects and pending jobs. */
export function DashboardScreen() {
  const { tenantRole } = useSessionClaims();
  const isAdmin = hasTenantRole(tenantRole, 'admin');
  const mine = useTicketCount(MY_OPEN);
  const unassigned = useTicketCount(UNASSIGNED);
  const recent = useTicketSearch(RECENT);
  const projects = useProjects({ pageSize: 50 });
  const queued = useJobs({ surface: 'portal', status: 'queued', pageSize: 50 });
  const running = useJobs({ surface: 'portal', status: 'running', pageSize: 50 });
  const pendingJobs = queued.items.length + running.items.length;
  const pendingMore = queued.hasMore || running.hasMore;

  return (
    <Page>
      <PageHeader
        title="Dashboard"
        description="What needs your attention right now."
        actions={
          <Link to="/queue" search={{}} className={buttonVariants({ variant: 'default' })}>
            Open the queue
            <ArrowRight />
          </Link>
        }
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={UserRound}
          title="My open tickets"
          description="Assigned to you and not done."
          value={countValue(mine.data)}
          isPending={mine.isPending}
          error={mine.error}
        />
        <StatCard
          icon={UserRoundX}
          title="Unassigned"
          description="Open tickets without an assignee in your projects."
          value={countValue(unassigned.data)}
          isPending={unassigned.isPending}
          error={unassigned.error}
        />
        {isAdmin ? (
          <>
            <StatCard
              icon={FolderKanban}
              title="Projects"
              description="Configured projects in this tenant."
              value={`${projects.items.filter((p) => p.archivedAt === null).length}${projects.hasMore ? '+' : ''}`}
              isPending={projects.isPending}
              error={projects.error}
              to={
                <Link to="/projects" search={{}} className="text-accent text-sm hover:underline">
                  Manage
                </Link>
              }
            />
            <StatCard
              icon={ListChecks}
              title="Pending jobs"
              description="Queued or running bulk actions and exports."
              value={`${pendingJobs}${pendingMore ? '+' : ''}`}
              isPending={queued.isPending || running.isPending}
              error={queued.error ?? running.error}
              to={
                <Link to="/jobs" className="text-accent text-sm hover:underline">
                  View
                </Link>
              }
            />
          </>
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Card>
          <CardHeader>
            <CardTitle>Recently updated</CardTitle>
            <CardDescription>
              The last ten tickets that changed, across your projects.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recent.error ? <ProblemBanner error={recent.error} size="sm" /> : null}
            {recent.isPending ? (
              <div className="space-y-2" aria-busy="true">
                {['a', 'b', 'c', 'd', 'e'].map((k) => (
                  <Skeleton key={k} className="h-8 w-full" />
                ))}
              </div>
            ) : recent.items.length === 0 ? (
              <p className="text-fg-muted text-sm">No tickets yet.</p>
            ) : (
              <ul className="divide-y divide-border">
                {recent.items.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                    <TicketKeyLink ticketKey={t.key} className="w-20 shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-fg" title={t.summary}>
                      {t.summary}
                    </span>
                    <StatusBadge label={t.display?.status_id} />
                    <UserChip name={t.display?.assignee_id} className="w-36" />
                    <time dateTime={t.updatedAt} className="w-24 text-right text-fg-muted text-xs">
                      {relativeTime(t.updatedAt)}
                    </time>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Projects</CardTitle>
            <CardDescription>Quick links to the projects you can see.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {projects.error ? <ProblemBanner error={projects.error} size="sm" /> : null}
            {projects.isPending ? <Skeleton className="h-24 w-full" /> : null}
            {projects.isSuccess && projects.items.length === 0 ? (
              <p className="text-fg-muted text-sm">You are not a member of any project yet.</p>
            ) : null}
            <ul className="space-y-1">
              {projects.items
                .filter((p) => p.archivedAt === null)
                .map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: p.id }}
                      className="flex min-w-0 items-center gap-2 text-fg hover:text-accent hover:underline"
                    >
                      <Badge variant="outline" className="font-mono">
                        {p.key}
                      </Badge>
                      <span className="truncate">{p.name}</span>
                    </Link>
                    {p.canAdmin ? <span className="text-fg-muted text-xs">admin</span> : null}
                  </li>
                ))}
            </ul>
            {projects.hasMore ? (
              <button
                type="button"
                className="text-accent text-xs hover:underline"
                onClick={projects.loadMore}
              >
                Show more
              </button>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}
